import { ChangeDetectionStrategy, Component, OnInit, OnChanges, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService, OrderUtilityService, OrderControllerService } from './../../../services';
import { User, OrderShipment, OrderLine } from './../../../models';
import { MediaType, OrderScreen, OrderType, LineStatus, OrderSourceSystem, SourceSystem } from '../../../models/enums';
import { OrderShipmentSelection } from 'src/app/modules/order-shipments/models/order-shipment-selection';
import { OrderIssuesComponent } from '../order-issues/order-issues.component';
import { MatIcon } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { CopyableDirective } from '../../directives/copyable-directive';
import { OrderLineLanguagesComponent } from '../order-line-languages/order-line-languages.component';

@Component({
  selector: 'insight-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OrderIssuesComponent, MatIcon, DatePipe, CopyableDirective, OrderLineLanguagesComponent]
})
export class OrderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orderScreen: OrderScreen;
  @Input() orderShipment: OrderShipment;
  @Input() orderLine: OrderLine;
  @Input() isSelected = false;

  @Output() selectedOrderEvent = new EventEmitter<any>();

  public orderTypeEnum = OrderType;
  public orderScreenEnums = OrderScreen;
  public lineStatusEnums = LineStatus;
  public dynamicsSourceSystem = OrderSourceSystem.Dynamics;
  public user: User;

  // computed once per input change instead of calling the methods from the template,
  // which re-ran them (and allocated Date objects) per row on every change detection cycle
  public orderLineClass = '';
  public shipDateClass = '';
  public mediaIcon = '';
  public mediaIconTooltip = '';

  private unsubscribeTrigger$ = new Subject();

  constructor(
    private orderControllerService: OrderControllerService,
    public orderUtilityService: OrderUtilityService,
    private userService: UserService) { }

  ngOnInit() {
    this.userService.getUser()
      .pipe(takeUntil(this.unsubscribeTrigger$))
      .subscribe(user => {
        this.user = user;
      });
  }

  ngOnChanges() {
    if (this.orderShipment) {
      this.orderLineClass = this.setOrderLineClass(this.orderShipment.orderType);
      this.shipDateClass = this.pastShipDateClass(this.orderShipment);
      this.mediaIcon = this.setOrderShipmentIcon(this.orderShipment.mediaType);
      this.mediaIconTooltip = this.iconTooltip(this.orderShipment);
    }
  }

  ngOnDestroy() {
    this.unsubscribeTrigger$.next();
    this.unsubscribeTrigger$.complete();
  }

  public setOrderLineClass(orderType: OrderType): string {
    let classString = 'order-line ';

    switch (orderType) {
      case OrderType.Adebs:
        classString += 'order-adebs';
        break;
      case OrderType.Cinelink:
        classString += 'order-cinelink';
        break;
      case OrderType.StreamingServer:
        classString += 'order-streaming';
        break;
      case OrderType.Unknown:
      default:
        classString += 'order-none';
        break;
    }

    // is order selected among all the orders?
    if (this.isSelected) {
      classString += ' selected-item';
    }

    return classString;
  }

  public linkToPubAdmin(productNumber: string) {
    this.orderControllerService.linkToPubAdmin(productNumber);
  }

  public pastShipDateClass(shipment: OrderShipment): string {
    const isDateBeforeToday = new Date(shipment.shipDate).getTime() < new Date().getTime();

    if (shipment.status === LineStatus.Complete ||
      shipment.status === LineStatus.Shipped ||
      shipment.status === LineStatus.PendingShipped) {
      return ''; // do not need to put past-due (highlighting red on complete orders)
    }

    return isDateBeforeToday ? 'past-due' : '';
  }

  public setOrderShipmentIcon(mediaType: MediaType): string {
    switch (mediaType) {
      case MediaType.Cvd:
        return 'album';
      case MediaType.Electronic:
        return 'cloud_download';
      case MediaType.NetworkDelivery:
        return 'cloud_download';
      case MediaType.HardDrive:
        return 'usb';
      case MediaType.Unknown:
        return '';
      default:
        return '';
    }
  }

  public selectedOrder(orderNumber: number, shipmentNumber: number, sourceSystem: SourceSystem): void {
    const data: OrderShipmentSelection = {
      orderNumber,
      shipmentNumber,
      sourceSystem
    };

    this.selectedOrderEvent.emit(data);
  }

  public iconTooltip(shipment: OrderShipment): string {
    switch (shipment.mediaType) {
      case MediaType.Cvd:
        return 'CVD';
      case MediaType.Electronic:
        return 'Electronic';
      case MediaType.NetworkDelivery:
        return 'Electronic';
      case MediaType.HardDrive:
        return 'Hard drive';
      default:
        return 'Unknown media type';
    }
  }
}
